import { Field, FormConsumer, FormProvider, ObjectField, observer } from "@formily/react";
import { Button } from "../../components/core/basic/Button";
import { useFormily } from "../../hooks/useFormily";

import { onFieldInputValueChange } from "@formily/core";
import { useFormSubmitErrorHandler } from "../../hooks/useFormSubmitErrorHandler";
import { useQuery } from "@tanstack/react-query";
import { endOfMonth, formatISO, startOfMonth, subMonths, subYears } from "date-fns";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Popover, TooltipTrigger } from "react-aria-components";
import { MdOutlineIosShare } from "react-icons/md";
import { css, cx } from "../../../styled-system/css";
import { Center, Grid, HStack, VStack } from "../../../styled-system/jsx";
import { OverlayArrow, Tooltip } from "../../components/core/basic/Tooltip";
import { Datepicker } from "../../components/fields/base/Datepicker";
import { CurrencyInput, NumberInput, OffsetInput } from "../../components/fields/base/NumberInput";
import { Select } from "../../components/fields/base/Select";
import {
  CurrencyInput as CurrencyInputF,
  NumberInput as NumberInputF,
} from "../../components/fields/formily/NumberInput";
import { popoverStyles } from "../../components/fields/styles/styles";
import type { Offset } from "../../zodios/apiDefinitions/pricing";
import { useGetV4CategoryReportTotals } from "../perf_reports/api/category/v4CategoryReportTotals";
import { applyOffset } from "../pricingv2/utils";
import { vstack } from "../../../styled-system/patterns";
import { formField } from "../../../styled-system/recipes";
import { useSimpleCan } from "../../config/casl/casl";
import { useDateFormatter } from "../../i18n/useDateFormatter";

export interface Goal {
  total_sales: number;
  tacos: number;
  ad_spend: number;
}

interface Props {
  initialValues?: Record<string, Goal>;
  onSubmit: (values: Record<string, Goal>) => void | Promise<void>;
  onCancel?: () => void | Promise<void>;
  displayYear: number;
}

export function GoalsConfigForm({ initialValues, onSubmit, displayYear, onCancel }: Props) {
  const { t } = useTranslation(["goals", "common"]);
  const handleSubmitError = useFormSubmitErrorHandler();
  const userCanUpdateGoals = useSimpleCan("update", "Goal");

  const form = useFormily<{ perPeriodGoals: Record<string, Goal> }>({
    initialValues: { perPeriodGoals: initialValues },
    readPretty: !userCanUpdateGoals,
    effects() {
      onFieldInputValueChange("perPeriodGoals.*.total_sales", (field) => {
        const tacos = field.query(".tacos").value();
        const ad_spend_field = field.query(".ad_spend").take();
        ad_spend_field?.setState((state) => {
          state.value = field.value * (tacos / 100);
        });
      });
      onFieldInputValueChange("perPeriodGoals.*.tacos", (field) => {
        const total_sales = field.query(".total_sales").value();
        const ad_spend_field = field.query(".ad_spend").take();
        ad_spend_field?.setState((state) => {
          state.value = total_sales * (field.value / 100);
        });
      });
      onFieldInputValueChange("perPeriodGoals.*.ad_spend", (field) => {
        const total_sales = field.query(".total_sales").value();
        const tacos_field = field.query(".tacos").take();
        tacos_field?.setState((state) => {
          state.value = (field.value / total_sales) * 100;
        });
      });
    },
  });
  return (
    <FormProvider form={form}>
      <VStack alignItems={"stretch"} paddingX={"xlarge"}>
        <GoalsConfigFormBody displayYear={displayYear} />
        <Center gap="small">
          {onCancel && (
            <Button
              variant="secondary"
              onPress={() => {
                void onCancel();
              }}
            >
              {t(($) => $.common.actions["Cancel"])}
            </Button>
          )}
          <FormConsumer>
            {(form) => (
              <Button
                isDisabled={form.invalid || !userCanUpdateGoals}
                isPending={form.submitting}
                onPress={() => {
                  form
                    .submit(async (values) => {
                      await onSubmit(values.perPeriodGoals);
                    })
                    .catch(handleSubmitError);
                }}
              >
                {t(($) => $.common.actions["Submit"])}
              </Button>
            )}
          </FormConsumer>
        </Center>
      </VStack>
    </FormProvider>
  );
}

const GoalsConfigFormBody = observer(({ displayYear }: { displayYear: number }) => {
  const { t } = useTranslation(["goals"]);
  const inputClasses = formField();
  const userCanUpdateGoals = useSimpleCan("update", "Goal");
  const monthOnly = useDateFormatter("monthOnly");

  const periods = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

  return (
    <ObjectField name="perPeriodGoals">
      {() => {
        return (
          <>
            <Grid gap="small" gridTemplateColumns="1fr 1fr 1fr 1fr 32px">
              <span className={inputClasses.label}>{t(($) => $.goals["Period"])}</span>
              <span className={inputClasses.label}>{t(($) => $.goals["Total Sales"])}</span>
              <span className={inputClasses.label}>{t(($) => $.goals["TACoS"])}</span>
              <span className={inputClasses.label}>{t(($) => $.goals["Ad Spend"])}</span>
              <span />
            </Grid>
            <VStack gap="xsmall" padding="xsmall" overflowY={"auto"}>
              {periods.map((period) => {
                const periodDate = new Date(displayYear, period);
                const periodKey = formatISO(periodDate, { representation: "date" });
                const periodLabel = monthOnly.format(periodDate);
                const rowRef = useRef<HTMLDivElement>(null);
                const [isPopoverOpen, setIsPopoverOpen] = useState(false);

                return (
                  <Grid
                    ref={rowRef}
                    key={periodKey}
                    gap="small"
                    gridTemplateColumns="1fr 1fr 1fr 1fr 32px"
                    outline={isPopoverOpen ? "2px solid {colors.trellisPalette.0}" : "none"}
                    width="100%"
                    fontSize="medium"
                  >
                    <span>{periodLabel}</span>
                    <ObjectField name={periodKey}>
                      {(field) => (
                        <>
                          <Field
                            name="total_sales"
                            component={[CurrencyInputF, { "aria-label": t(($) => $.goals["Total Sales goal"]) }]}
                            initialValue={NaN}
                          />
                          <Field
                            name="tacos"
                            component={[NumberInputF, { endIcon: "%", "aria-label": t(($) => $.goals["TACOS goal"]) }]}
                            initialValue={NaN}
                          />
                          <Field
                            name="ad_spend"
                            component={[CurrencyInputF, { "aria-label": t(($) => $.goals["Ad Spend goal"]) }]}
                            initialValue={NaN}
                          />
                          {userCanUpdateGoals && (
                            <TooltipTrigger>
                              <Button variant="contentOnly" onPress={() => setIsPopoverOpen(true)} fontSize="large">
                                <MdOutlineIosShare
                                  className={css({
                                    transform: "rotate(-90deg)",
                                  })}
                                />
                              </Button>
                              <Tooltip>{t(($) => $.goals["Prefill goals using values from other periods"])}</Tooltip>
                            </TooltipTrigger>
                          )}

                          <Popover
                            triggerRef={rowRef}
                            isOpen={isPopoverOpen}
                            onOpenChange={setIsPopoverOpen}
                            className={cx(popoverStyles)}
                          >
                            <OverlayArrow color={"white"} />
                            <PrefillDialog
                              targetPeriod={periodDate}
                              onApply={(vals) => {
                                field.setValue(vals);
                                setIsPopoverOpen(false);
                              }}
                            />
                          </Popover>
                        </>
                      )}
                    </ObjectField>
                  </Grid>
                );
              })}
            </VStack>
          </>
        );
      }}
    </ObjectField>
  );
});

function PrefillDialog({
  targetPeriod,
  onApply,
}: {
  targetPeriod: Date;
  onApply: (values: { total_sales: number; ad_spend: number; tacos: number }) => void;
}) {
  const { t } = useTranslation(["goals", "common"]);
  const [prefillType, setPrefillType] = useState("yoy");
  const [referencePeriod, setReferencePeriod] = useState(subYears(targetPeriod, 1));
  const [totalSalesOffset, setTotalSalesOffset] = useState<Offset>({ amount: 0, type: "percent" });
  const [adSpendOffset, setAdSpendOffset] = useState<Offset>({ amount: 0, type: "percent" });

  const totalsQuery = useQuery(
    useGetV4CategoryReportTotals()({
      start: startOfMonth(referencePeriod),
      end: endOfMonth(referencePeriod),
      preStart: startOfMonth(referencePeriod),
      preEnd: endOfMonth(referencePeriod),
    }),
  );

  const totalSalesGoal =
    totalsQuery.data?.data.total_sales ?
      applyOffset(totalsQuery.data.data.total_sales, totalSalesOffset.amount, totalSalesOffset.type)
    : NaN;

  const adSpendGoal =
    totalsQuery.data?.data.cost ?
      applyOffset(totalsQuery.data.data.cost, adSpendOffset.amount, adSpendOffset.type)
    : NaN;

  return (
    <Dialog
      className={vstack({
        padding: "xlarge",
      })}
    >
      <HStack gap="large" alignItems="center">
        <Select
          label={t(($) => $.goals["Reference Period"])}
          css={{ width: "300px" }}
          items={[
            { value: "yoy", label: t(($) => $.goals["Year over Year"]) },
            { value: "mom", label: t(($) => $.goals["Month over Month"]) },
            { value: "custom", label: t(($) => $.goals["Custom"]) },
          ]}
          selectedKey={prefillType}
          onSelectionChange={(val) => {
            setPrefillType(val as string);
            if (val === "yoy") {
              setReferencePeriod(subYears(targetPeriod, 1));
            } else if (val === "mom") {
              setReferencePeriod(subMonths(targetPeriod, 1));
            }
          }}
        />
        <Datepicker
          disabled={prefillType !== "custom"}
          label="&nbsp;"
          value={referencePeriod.toISOString()}
          onChange={(val) => setReferencePeriod(new Date(val!))}
          showMonthYearPicker
          dateFormat={"yyyy, MMMM"}
        />
      </HStack>
      <HStack>
        <CurrencyInput
          label={t(($) => $.goals["Ref. Total Sales"])}
          isDisabled
          value={totalsQuery.data?.data.total_sales ?? NaN}
        />
        <OffsetInput label={t(($) => $.goals["Offset By"])} value={totalSalesOffset} onChange={setTotalSalesOffset} />
        <CurrencyInput label={t(($) => $.goals["Total Sales Goal"])} isDisabled value={totalSalesGoal} />
      </HStack>
      <HStack>
        <CurrencyInput
          label={t(($) => $.goals["Ref. Ad Spend"])}
          isDisabled
          value={totalsQuery.data?.data.cost ?? NaN}
        />
        <OffsetInput label={t(($) => $.goals["Offset By"])} value={adSpendOffset} onChange={setAdSpendOffset} />
        <CurrencyInput label={t(($) => $.goals["Ad Budget"])} isDisabled value={adSpendGoal} />
      </HStack>
      <NumberInput
        label={t(($) => $.goals["TACoS Goal"])}
        endIcon="%"
        value={adSpendGoal && totalSalesGoal ? (adSpendGoal / totalSalesGoal) * 100 : NaN}
        isDisabled
      />
      <Center>
        <Button
          onPress={() =>
            onApply({
              total_sales: totalSalesGoal,
              ad_spend: adSpendGoal,
              tacos: totalSalesGoal !== 0 ? (adSpendGoal / totalSalesGoal) * 100 : 0,
            })
          }
        >
          {t(($) => $.common.actions["Apply"])}
        </Button>
      </Center>
    </Dialog>
  );
}
